/**
 * SupplierBolPreviewPage.jsx
 * ──────────────────────────
 * Screen 5 — BOL Preview document.
 * Route: /supplier/disposal/:projectId/bol/:bolId/preview
 *
 * All data comes from location.state (passed by SupplierLoadingPage).
 * The BOL may be offline-queued and not yet on the server — no API fetch.
 */

import React, { useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../shared/context/UserContext';
import SupplierLayout from '../../layouts/SupplierLayout';
import './SupplierBolPreviewPage.css';

function fmtDate() {
  return new Date().toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function SupplierBolPreviewPage() {
  const { projectId, bolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { callerEmail } = useUser();

  const { bol, project, loadedTools, isAmendedBol } = location.state || {};

  // Tool list and weight from passed state
  const bolTools = useMemo(() => {
    if (!loadedTools) return [];
    return loadedTools;
  }, [loadedTools]);

  const totalWeight = useMemo(() => {
    return bolTools.reduce((sum, t) => sum + (t.estimated_weight || t.weight_lbs || 0), 0);
  }, [bolTools]);

  const bolNumber = bol?.bol_number || bolId?.substring(0, 12) || '—';
  const backUrl = `/supplier/disposal/${projectId}/load/${bolId}`;

  // Missing state — can't render
  if (!bol || !project) {
    return (
      <SupplierLayout>
        <div className="bp-header">
          <button className="bp-back" onClick={() => navigate('/supplier/disposal')}>&larr;</button>
          <span className="bp-header-title">BOL Preview</span>
          <span className="bp-header-logo">TMG</span>
        </div>
        <div className="bp-state bp-state-err">
          Session data not found. Please go back and try again.
        </div>
        <div style={{ textAlign: 'center', padding: 16 }}>
          <button className="bp-btn-secondary" onClick={() => navigate('/supplier/disposal')}>
            &larr; Back to Projects
          </button>
        </div>
      </SupplierLayout>
    );
  }

  return (
    <SupplierLayout>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="bp-header">
        <button className="bp-back" onClick={() => isAmendedBol ? navigate(-1) : navigate(backUrl)}>&larr;</button>
        <span className="bp-header-title">BOL Preview</span>
        <span className="bp-header-logo">TMG</span>
      </div>

      {/* ── Amended banner ────────────────────────────────────────────── */}
      {isAmendedBol && (
        <div className="bp-amended-banner">
          AMENDED BILL OF LADING — {bolNumber}-A
        </div>
      )}

      {/* ── Document body ─────────────────────────────────────────────── */}
      <div className="bp-scroll">
        <div className="bp-doc">
          {/* Section 1 — Header block */}
          <div className="bp-doc-header">
            <div className="bp-doc-company">TMG TOOLING MANAGEMENT GROUP</div>
            <div className="bp-doc-type">BILL OF LADING</div>
            <div className="bp-doc-bol-row">
              <span className="bp-doc-bol-label">BOL Number:</span>
              <span className="bp-doc-bol-number">
                {isAmendedBol ? `${bolNumber}-A` : bolNumber}
              </span>
            </div>
            <div className="bp-doc-generated">Generated: {fmtDate()}</div>
          </div>

          {/* Section 2 — Project info */}
          <div className="bp-section">
            <div className="bp-section-title">Project Information</div>
            <div className="bp-grid">
              <div className="bp-field"><span className="bp-label">Project Number</span><span className="bp-value">{project.project_number || '—'}</span></div>
              <div className="bp-field"><span className="bp-label">OEM</span><span className="bp-value">{project.oem || '—'}</span></div>
              <div className="bp-field"><span className="bp-label">Site</span><span className="bp-value">{project.site_name || '—'}</span></div>
            </div>
          </div>

          {/* Section 3 — Carrier info */}
          <div className="bp-section">
            <div className="bp-section-title">Carrier Information</div>
            <div className="bp-grid">
              <div className="bp-field"><span className="bp-label">Truck Type</span><span className="bp-value">{bol.truck_type || '—'}</span></div>
              <div className="bp-field"><span className="bp-label">Hauler Company</span><span className="bp-value">{bol.hauler_company || '—'}</span></div>
              <div className="bp-field"><span className="bp-label">{bol.truck_type === 'ROLL-OFF' ? 'Container #' : 'Truck #'}</span><span className="bp-value">{bol.truck_number || '—'}</span></div>
              <div className="bp-field"><span className="bp-label">Driver</span><span className="bp-value">{bol.driver_name || '—'}</span></div>
              <div className="bp-field"><span className="bp-label">Advisory Weight</span><span className="bp-value">{bol.advisory_weight_lbs ? `${Number(bol.advisory_weight_lbs).toLocaleString()} lbs` : '—'}</span></div>
            </div>
          </div>

          {/* Section 4 — Tool list */}
          <div className="bp-section">
            <div className="bp-section-title">
              Tools on this shipment ({bolTools.length} tool{bolTools.length !== 1 ? 's' : ''})
            </div>
            <table className="bp-tool-table">
              <thead>
                <tr>
                  <th>Tool ID</th>
                  <th className="bp-col-right">Est. Weight (lbs)</th>
                </tr>
              </thead>
              <tbody>
                {bolTools.length === 0 ? (
                  <tr><td colSpan={2} className="bp-tool-empty">No tools on this BOL yet.</td></tr>
                ) : (
                  bolTools.map(t => {
                    const w = t.estimated_weight || t.weight_lbs;
                    return (
                      <tr key={t.id || t.tool_id}>
                        <td className="bp-tool-id">{t.supplier_tool_id || t.id || t.tool_id}</td>
                        <td className="bp-col-right">{w ? Number(w).toLocaleString() : '—'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {bolTools.length > 0 && (
                <tfoot>
                  <tr className="bp-total-row">
                    <td className="bp-total-label">Total Estimated Weight</td>
                    <td className="bp-col-right bp-total-value">{totalWeight.toLocaleString()} lbs</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Section 5 — Disclaimer */}
          <div className="bp-disclaimer">
            Weight estimates are based on available data and may vary.
            Actual weights determined at recycler weigh station.
          </div>

          {/* Section 6 — Operator info */}
          <div className="bp-operator">
            <div>Prepared by: {callerEmail}</div>
            <div>{fmtDate()}</div>
          </div>
        </div>
      </div>

      {/* ── Action buttons ────────────────────────────────────────────── */}
      <div className="bp-actions">
        {!isAmendedBol && (
          <button className="bp-btn-secondary" onClick={() => navigate(backUrl)}>
            &larr; Back to Loading
          </button>
        )}
        <button className="bp-btn-primary"
          onClick={() => navigate(`/supplier/disposal/${projectId}/bol/${bolId}/sign`, { state: location.state })}>
          Get Driver's Signature &rarr;
        </button>
      </div>
    </SupplierLayout>
  );
}
